import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import { useState, useContext } from 'react';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useNavigate } from 'react-router';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { customAlphabet } from 'nanoid';
import Chip from '@mui/material/Chip';
import dayjs from 'dayjs';
import Snackbar from '@mui/material/Snackbar';

import useAlertForm from './useAlertForm';
import SelectOptions from './SelectOptions';
import { ClinicRxContext } from '../../App';
import { createHistoryAction, formulateUserObject } from '../custom';
import { getServerURL } from '../../utils';
import { isAlertOnAnyDashboard } from '../../../../api-server/alertFilter';
import useEmitAlert from '../../socket/hooks/useEmitAlert';

const SERVER_URL = getServerURL();
const DEFAULT_MESSAGE = 'Internal server error.';
const INDICATOR_CHIPS = [
  'CTI',
  'IOP',
  'PROS',
  'Partial-Hospital',
  'OnTrack',
  'Day-Rehab',
  'MAT',
  'OTP',
];
const CO_OCCURRING = 'CO';
const MENTAL_HEALTH = 'MH';
const SUBSTANCE_USE = 'SU';

export default function ModalElement({
  open,
  handleClose,
  displayName,
  alertType,
}) {
  const { state, fetchProtected } = useContext(ClinicRxContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const formState = useAlertForm();
  const [snackbar, setSnackbar] = useState({ isOpen: false, message: '' });
  const { sendNewAlertDataToSocket } = useEmitAlert();

  const getDiagnosisCodes = () => {
    const codes = [];
    for (const code of ['diagnosisCode', 'secondDiagnosisCode']) {
      if (formState[code] !== '') {
        codes.push(formState[code]);
      }
    }
    return codes;
  };

  const resetState = () => {
    formState.setInsuranceProvider('');
    formState.setPreferredLanguage('');
    formState.setExistingClinic('');
    formState.setIsPeerRequested(false);
    formState.setIsTelehealth(false);
    formState.setIsInjectable(false);
    formState.setIsMinor(false);
    formState.setIsCTI(false);
    formState.setAlertZIP('');
    formState.setIsIOP(false);
    formState.setIsPROS(false);
    formState.setIsPartialHospital(false);
    formState.setIsOnTrack(false);
    formState.setDiagnosisCode('');
    formState.setDischargeDate(dayjs());
    formState.setSecondDiagnosisCode('');
    formState.setOtherInsuranceOption('');
    formState.setIsDayRehab(false);
    formState.setIsMAT(false);
    formState.setIsOTP(false);
  };

  const closeAndReset = () => {
    resetState();
    handleClose();
  };

  const calculateStartingStatus = (data) => {
    if (!!data.existingClinic) {
      return 1;
    }
    if (!isAlertOnAnyDashboard(data)) {
      return 2;
    }
    return 0;
  };

  const handleClick = async () => {
    if (
      !formState.insuranceProvider ||
      (formState.insuranceProvider === 'Other' &&
        !formState.otherInsuranceOption)
    )
      return;
    if (!formState.alertZIP) return;
    const nanoid = customAlphabet('0123456789ABCDEF', 6);
    const currentDay = dayjs().format('MM/DD/YYYY');
    const data = {
      alertID: nanoid(),
      alertDate: currentDay,
      alertStatus: undefined,
      alertType: alertType,
      alertZIP: formState.alertZIP,
      insuranceProvider:
        formState.insuranceProvider === 'Other'
          ? `Other-${formState.otherInsuranceOption.trim()}`
          : formState.insuranceProvider,
      dischargeDate: formState.dischargeDate.format('MM/DD/YYYY'),
      preferredLanguage: formState.preferredLanguage || null,
      existingClinic: formState.existingClinic || null,
      facilityContact: {
        facilityContactFullName: state.user.name.first.concat(
          ` ${state.user.name.last}`,
        ),
        facilityContactPhoneNumber: state.user.phoneNumber,
        facilityContactEmail: state.user.email,
        facilityContactOrganization: state.user.organization,
      },
      indicators: {
        isPeerRequested: formState.isPeerRequested,
        isTelehealth: formState.isTelehealth,
        isInjectable: formState.isInjectable,
        isMinor: formState.isMinor,
        isCTI: formState.isCTI,
        isIOP: formState.isIOP,
        isPROS: formState.isPROS,
        isPartialHospital: formState.isPartialHospital,
        isOnTrack: formState.isOnTrack,
        isDayRehab: formState.isDayRehab,
        isMAT: formState.isMAT,
        isOTP: formState.isOTP,
      },
      diagnosisCodes: getDiagnosisCodes(),
      actionHistory: {
        actions: [
          createHistoryAction(
            'create-alert',
            formulateUserObject(state.user),
            'nothing',
            'new alert',
          ),
        ],
      },
      appointmentDate: null,
      unclaimedClinics: [],
      isTreeOnDashboard: !!formState.existingClinic,
    };
    data.alertStatus = calculateStartingStatus(data);

    setIsLoading(true);
    try {
      const response = await fetchProtected((bearerToken) =>
        fetch(`${SERVER_URL}/create-alert`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            authorization: bearerToken,
          },
          body: JSON.stringify({ alert: data }),
        }),
      );
      if (response.ok) {
        sendNewAlertDataToSocket(data);
        setIsLoading(false);
        closeAndReset();
        navigate('/dashboard');
      } else {
        let message = await response.text();
        if (!message) {
          message = DEFAULT_MESSAGE;
        }
        setSnackbar({
          isOpen: true,
          message,
        });
        setIsLoading(false);
      }
    } catch (err) {
      setSnackbar({
        isOpen: true,
        message: DEFAULT_MESSAGE,
      });
      setIsLoading(false);
      console.log(`Error sending POST request to /create-alert: ${err}`);
    }
  };

  const chipToStateMap = {
    CTI: {
      state: formState.isCTI,
      setterFunction: formState.setIsCTI,
    },
    IOP: {
      state: formState.isIOP,
      setterFunction: formState.setIsIOP,
    },
    PROS: {
      state: formState.isPROS,
      setterFunction: formState.setIsPROS,
    },
    'Partial-Hospital': {
      state: formState.isPartialHospital,
      setterFunction: formState.setIsPartialHospital,
    },
    OnTrack: {
      state: formState.isOnTrack,
      setterFunction: formState.setIsOnTrack,
    },
    'Day-Rehab': {
      state: formState.isDayRehab,
      setterFunction: formState.setIsDayRehab,
    },
    MAT: {
      state: formState.isMAT,
      setterFunction: formState.setIsMAT,
    },
    OTP: {
      state: formState.isOTP,
      setterFunction: formState.setIsOTP,
    },
  };

  const peerDisabled = formState.isCTI || formState.isOnTrack;
  const isChipRelevant = (option) => {
    if (alertType === CO_OCCURRING) return true;
    if (alertType === MENTAL_HEALTH) {
      if (
        ['CTI', 'IOP', 'PROS', 'Partial-Hospital', 'OnTrack'].includes(option)
      ) {
        return true;
      }
    }
    if (alertType === SUBSTANCE_USE) {
      if (['Day-Rehab', 'MAT', 'OTP'].includes(option)) {
        return true;
      }
    }
    return false;
  };

  return (
    <>
      <Modal
        open={open}
        onClose={closeAndReset}
        aria-labelledby={`Request a ${displayName} appointment`}
        aria-describedby={`Form to complete a ${displayName} appointment request`}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '600px',
            bgcolor: 'background.paper',
            border: `2px solid ${theme.palette.primary.dark}`,
            boxShadow: 24,
            p: 4,
            maxHeight: '90vh',
            overflowY: 'scroll',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Box
              component='form'
              sx={{
                '& > :not(style)': { m: 1, width: '30ch' },
                display: 'flex',
                flexDirection: 'column',
                gap: '5px',
              }}
            >
              {alertType === CO_OCCURRING && (
                <Alert severity='info'>Combined SU and MH needs.</Alert>
              )}
              <FormGroup
                sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
              >
                <Box
                  sx={{ display: 'flex', flexDirection: 'row', gap: '20px' }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formState.isTelehealth}
                        slotProps={{ input: { 'aria-label': 'controlled' } }}
                        onChange={() =>
                          formState.setIsTelehealth((prev) => !prev)
                        }
                      />
                    }
                    label='Telehealth'
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        disabled={peerDisabled}
                        checked={!peerDisabled && formState.isPeerRequested}
                        slotProps={{ input: { 'aria-label': 'controlled' } }}
                        onChange={() =>
                          formState.setIsPeerRequested((prev) => !prev)
                        }
                      />
                    }
                    label='Peer Needed'
                  />
                </Box>
              </FormGroup>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '10px',
                  flexWrap: 'wrap',
                }}
              >
                {INDICATOR_CHIPS.map((option) => {
                  if (!isChipRelevant(option)) return null;
                  let displayName = option;
                  if (displayName.includes('-')) {
                    displayName = displayName.replace('-', ' ');
                  }
                  return (
                    <Chip
                      key={option}
                      label={displayName}
                      color={
                        chipToStateMap[option].state ? 'primary' : 'default'
                      }
                      onClick={() => {
                        if (option === 'CTI' || option === 'OnTrack') {
                          formState.setIsPeerRequested(false);
                        }
                        chipToStateMap[option].setterFunction((prev) => !prev);
                      }}
                    />
                  );
                })}
              </Box>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
                >
                  <DatePicker
                    label='Discharge Date'
                    value={formState.dischargeDate}
                    onChange={(newValue) => {
                      formState.setDischargeDate(newValue);
                    }}
                  />
                </Box>
              </LocalizationProvider>
              <SelectOptions
                existingClinic={formState.existingClinic}
                setExistingClinic={formState.setExistingClinic}
                alertZIP={formState.alertZIP}
                setAlertZIP={formState.setAlertZIP}
                insuranceProvider={formState.insuranceProvider}
                setInsuranceProvider={formState.setInsuranceProvider}
                otherInsuranceOption={formState.otherInsuranceOption}
                setOtherInsuranceOption={formState.setOtherInsuranceOption}
                preferredLanguage={formState.preferredLanguage}
                setPreferredLanguage={formState.setPreferredLanguage}
                diagnosisCode={formState.diagnosisCode}
                setDiagnosisCode={formState.setDiagnosisCode}
                secondDiagnosisCode={formState.secondDiagnosisCode}
                setSecondDiagnosisCode={formState.setSecondDiagnosisCode}
                isCO={alertType === 'CO'}
              />
              <FormGroup
                sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
              >
                <Box
                  sx={{ display: 'flex', flexDirection: 'row', gap: '20px' }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formState.isMinor}
                        slotProps={{ input: { 'aria-label': 'controlled' } }}
                        onChange={() => formState.setIsMinor((prev) => !prev)}
                      />
                    }
                    label='Minor (Under 18)'
                  />
                </Box>
                <Box
                  sx={{ display: 'flex', flexDirection: 'row', gap: '20px' }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formState.isInjectable}
                        slotProps={{ input: { 'aria-label': 'controlled' } }}
                        onChange={() =>
                          formState.setIsInjectable((prev) => !prev)
                        }
                      />
                    }
                    label='Injectables'
                  />
                </Box>
              </FormGroup>
              <Button
                variant='contained'
                onClick={handleClick}
                loading={isLoading}
              >
                <SearchIcon sx={{ marginRight: '10px' }} />
                Find
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbar.isOpen}
        onClose={() => {
          setSnackbar({
            isOpen: false,
            message: '',
          });
        }}
        autoHideDuration={12000}
        key={'topcenter'}
      >
        <Alert severity='warning'>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
}
